import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type CampaignCodesInput, type CampaignDetail, type CampaignRow, type Me } from './api';
import { KAMPANYA_DURUMU, KAMPANYA_KAYNAK, KAMPANYA_METIN, KAMPANYA_RET, KOD_YAYIN_DURUMU } from './metin/kampanya';
import { indirimYaz, para, tamSayi, tarih, TANIMSIZ } from './metin/ortak';
import { paraAltBirime } from './quote-pricing';
import { TarihAraligiSecici } from './TarihSecici';
import { Alan, AlanSatiri } from './Workflow';

/**
 * Pazarlama: MBD'nin kendi kampanyaları (Ürün Sahibi, 07.10.2026).
 *
 * Liste influencer kampanyalarını da gösterir ama onlara dokunmaz: onları influencer platformu
 * yazar. Form yalnız iç kampanya kurar; influencer ataması ve komisyon alanı yoktur.
 * Sekmeyi gizlemek yetkilendirme değil (`Pazarlama.tsx`in aynı notu): uçlar sunucuda yeniden
 * kontrol ediliyor.
 */

const PARA_BIRIMLERI = ['EUR', 'USD', 'AED'] as const;
const KAYNAKLAR = ['ALL', 'INFLUENCER', 'INTERNAL'] as const;

function hataMetni(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) return (caught.reason !== undefined ? KAMPANYA_RET[caught.reason] : undefined) ?? caught.message;
  return fallback;
}

/** Bugünden `gun` gün sonrası, `YYYY-MM-DD` (UTC): API'nin tarih biçimi ve seçicinin değeri. */
function gunMetni(gun: number): string {
  return new Date(Date.now() + gun * 86_400_000).toISOString().slice(0, 10);
}

function kalanMetni(satir: CampaignRow): string {
  const metin = KAMPANYA_METIN.liste;
  const kalan = satir.remaining === null ? metin.sinirsiz : tamSayi(satir.remaining);
  if (satir.budgetMinor === null) return kalan;
  return `${kalan} · ${metin.butce} ${para(satir.budgetMinor, satir.discount.currency)}`;
}

function KampanyaAyrinti({ id, yazabilir, onKapat, onDegisti }: { id: string; yazabilir: boolean; onKapat: () => void; onDegisti: () => void }) {
  const [detay, setDetay] = useState<CampaignDetail | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [onPrefix, setOnPrefix] = useState('');
  const [adet, setAdet] = useState('10');
  const [mesgul, setMesgul] = useState(false);
  const metin = KAMPANYA_METIN.detay;

  const yukle = useCallback(async () => {
    try {
      setDetay(await api.campaign(id));
      setHata(null);
    } catch (caught) {
      setHata(hataMetni(caught, KAMPANYA_METIN.liste.hata));
    }
  }, [id]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  const isle = async (is: () => Promise<unknown>) => {
    setMesgul(true);
    setHata(null);
    try {
      await is();
      await yukle();
      onDegisti();
    } catch (caught) {
      setHata(hataMetni(caught, KAMPANYA_METIN.form.hata));
    } finally {
      setMesgul(false);
    }
  };

  const kodlar: CampaignCodesInput = { mode: 'BULK', prefix: onPrefix, count: Number.parseInt(adet, 10) };

  return (
    <section className="content-section" data-testid="campaign-detail" aria-labelledby="campaign-detail-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.baslik}</span>
          <h2 id="campaign-detail-title">{detay?.campaign.name ?? TANIMSIZ}</h2>
        </div>
        <button type="button" onClick={onKapat}>{metin.kapat}</button>
      </div>
      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      {detay ? (
        <>
          <div className="stat-row">
            <span>{metin.kapsam}: <strong>{detay.allCatalogue ? metin.tumKatalog : (detay.scope.productIds ?? []).join(', ')}</strong></span>
            <span>{metin.kodBasinaSinir}: <strong>{tamSayi(detay.perCodeLimit)}</strong></span>
            {detay.scope.departureFrom || detay.scope.departureUntil ? (
              <span>{metin.kalkis}: <strong>{detay.scope.departureFrom ?? TANIMSIZ} / {detay.scope.departureUntil ?? TANIMSIZ}</strong></span>
            ) : null}
            {detay.scope.minimumAmount !== undefined ? (
              <span>{metin.asgari}: <strong>{para(detay.scope.minimumAmount, detay.scope.currency)}</strong></span>
            ) : null}
          </div>
          {yazabilir ? (
            <div className="toolbar">
              <button
                type="button"
                disabled={mesgul}
                data-testid="campaign-toggle"
                onClick={() => void isle(() => api.setCampaignStatus(id, detay.campaign.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE'))}
              >
                {detay.campaign.status === 'ACTIVE' ? metin.askiyaAl : metin.devamEt}
              </button>
              <input aria-label={KAMPANYA_METIN.form.onEk} placeholder={KAMPANYA_METIN.form.onEk} value={onPrefix} onChange={(olay) => setOnPrefix(olay.target.value)} />
              <input aria-label={KAMPANYA_METIN.form.adet} value={adet} onChange={(olay) => setAdet(olay.target.value)} inputMode="numeric" />
              <button type="button" disabled={mesgul || onPrefix.trim() === ''} data-testid="campaign-add-codes" onClick={() => void isle(() => api.addCampaignCodes(id, kodlar))}>
                {metin.kodEkle}
              </button>
            </div>
          ) : null}
          <div className="table-wrap">
            <table data-testid="campaign-codes-table">
              <thead>
                <tr>
                  <th>{metin.sutunKod}</th>
                  <th>{metin.sutunDurum}</th>
                  <th className="money">{metin.sutunKullanim}</th>
                  <th>{metin.sutunYayin}</th>
                  <th>{metin.sutunSon}</th>
                </tr>
              </thead>
              <tbody>
                {detay.codes.length === 0 ? (
                  <tr><td colSpan={5} className="muted">{metin.bos}</td></tr>
                ) : (
                  detay.codes.map((kod) => (
                    <tr key={kod.code}>
                      <td data-label={metin.sutunKod}>{kod.code}</td>
                      <td data-label={metin.sutunDurum}>{KAMPANYA_DURUMU[kod.status] ?? kod.status}</td>
                      <td data-label={metin.sutunKullanim} className="money">{tamSayi(kod.confirmed)} / {kod.maxRedemptions === null ? KAMPANYA_METIN.liste.sinirsiz : tamSayi(kod.maxRedemptions)}</td>
                      <td data-label={metin.sutunYayin}>{KOD_YAYIN_DURUMU[kod.publishState] ?? kod.publishState}</td>
                      <td data-label={metin.sutunSon}>{tarih(kod.validUntil)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}

function KampanyaFormu({ onOlustu, onVazgec }: { onOlustu: () => void; onVazgec: () => void }) {
  const metin = KAMPANYA_METIN.form;
  const [ad, setAd] = useState('');
  const [tutar, setTutar] = useState('');
  const [paraBirimi, setParaBirimi] = useState<string>('USD');
  const [urunler, setUrunler] = useState<string[]>([]);
  const [urunGirdisi, setUrunGirdisi] = useState('');
  const [tumKatalog, setTumKatalog] = useState(false);
  const [kalkis, setKalkis] = useState({ from: '', to: '' });
  const [asgari, setAsgari] = useState('');
  const [sinir, setSinir] = useState('1');
  const [butce, setButce] = useState('');
  // Geçerlilik varsayılanı bugünden 90 gün: boş bırakılmış bir form süresiz kuponu değil, kısa
  // ömürlü bir kuponu önerir. Süre seçiciden değişir.
  const [gecerlilik, setGecerlilik] = useState(() => ({ from: gunMetni(0), to: gunMetni(90) }));
  const [kodKipi, setKodKipi] = useState<'NONE' | 'SINGLE' | 'BULK'>('BULK');
  const [kodAdi, setKodAdi] = useState('');
  const [onEk, setOnEk] = useState('');
  const [adet, setAdet] = useState('10');
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [sonuc, setSonuc] = useState<string | null>(null);

  const urunEkle = () => {
    const kimlik = urunGirdisi.trim();
    if (kimlik !== '' && !urunler.includes(kimlik)) setUrunler([...urunler, kimlik]);
    setUrunGirdisi('');
  };

  const gonder = async () => {
    setHata(null);
    setSonuc(null);
    if (ad.trim() === '') return setHata(metin.hataAd);
    if (gecerlilik.from === '' || gecerlilik.to === '') return setHata(metin.hataTarih);
    if ((kalkis.from === '') !== (kalkis.to === '')) return setHata(metin.hataKalkis);
    let tutarMinor: number;
    try {
      tutarMinor = paraAltBirime(tutar);
    } catch {
      return setHata(metin.hataTutar);
    }
    if (tutarMinor <= 0) return setHata(metin.hataTutar);

    const kodlar: CampaignCodesInput | undefined =
      kodKipi === 'SINGLE' ? { mode: 'SINGLE', code: kodAdi } : kodKipi === 'BULK' ? { mode: 'BULK', prefix: onEk, count: Number.parseInt(adet, 10) } : undefined;
    setMesgul(true);
    try {
      const sonuc_ = await api.createCampaign({
        name: ad.trim(),
        discountAmountMinor: tutarMinor,
        currency: paraBirimi,
        ...(urunler.length > 0 ? { productIds: urunler } : { allCatalogue: tumKatalog }),
        ...(kalkis.from !== '' ? { departureFrom: kalkis.from, departureUntil: kalkis.to } : {}),
        ...(asgari.trim() !== '' ? { minimumAmountMinor: paraAltBirime(asgari) } : {}),
        perCodeLimit: Number.parseInt(sinir, 10),
        ...(butce.trim() !== '' ? { budgetMinor: paraAltBirime(butce) } : {}),
        validFrom: new Date(`${gecerlilik.from}T00:00:00.000Z`).toISOString(),
        validUntil: new Date(`${gecerlilik.to}T23:59:59.000Z`).toISOString(),
        ...(kodlar ? { codes: kodlar } : {}),
      });
      const yayinlanamayan = sonuc_.codes.filter((kod) => kod.publishState !== 'PUBLISHED').length;
      setSonuc(`${metin.olustu} ${tamSayi(sonuc_.codes.length)} ${metin.uretilen}.${yayinlanamayan > 0 ? ` ${tamSayi(yayinlanamayan)} ${metin.yayinlanamayan}` : ''}`);
      onOlustu();
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    } finally {
      setMesgul(false);
    }
  };

  return (
    <section className="content-section" data-testid="campaign-form" aria-labelledby="campaign-form-title">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{metin.ustBaslik}</span>
          <h2 id="campaign-form-title">{metin.baslik}</h2>
          <p className="muted">{metin.tarif}</p>
        </div>
        <button type="button" onClick={onVazgec}>{metin.vazgec}</button>
      </div>
      {hata ? <div className="error callout" role="alert">{hata}</div> : null}
      {sonuc ? <div className="callout callout-ok" role="status">{sonuc}</div> : null}

      <AlanSatiri>
        <Alan etiket={metin.ad} zorunlu><input data-testid="campaign-name" value={ad} maxLength={120} onChange={(olay) => setAd(olay.target.value)} /></Alan>
        <Alan etiket={metin.tutar} zorunlu><input data-testid="campaign-amount" value={tutar} inputMode="decimal" onChange={(olay) => setTutar(olay.target.value)} /></Alan>
        <Alan etiket={metin.paraBirimi}>
          <select value={paraBirimi} onChange={(olay) => setParaBirimi(olay.target.value)}>
            {PARA_BIRIMLERI.map((deger) => <option key={deger} value={deger}>{deger}</option>)}
          </select>
        </Alan>
      </AlanSatiri>

      <AlanSatiri>
        <Alan etiket={metin.urunKapsami} ipucu={metin.urunIpucu}>
          <input
            data-testid="campaign-product-input"
            placeholder={metin.urunAra}
            value={urunGirdisi}
            onChange={(olay) => setUrunGirdisi(olay.target.value)}
            onKeyDown={(olay) => {
              if (olay.key === 'Enter') {
                olay.preventDefault();
                urunEkle();
              }
            }}
          />
        </Alan>
        <button type="button" onClick={urunEkle}>{metin.urunEkle}</button>
      </AlanSatiri>
      {urunler.length > 0 ? (
        <div className="stat-row" data-testid="campaign-products">
          {urunler.map((kimlik) => (
            <span key={kimlik}>{kimlik} <button type="button" onClick={() => setUrunler(urunler.filter((diger) => diger !== kimlik))}>{metin.urunKaldir}</button></span>
          ))}
        </div>
      ) : (
        <label>
          <input type="checkbox" checked={tumKatalog} onChange={(olay) => setTumKatalog(olay.target.checked)} data-testid="campaign-all-catalogue" /> {metin.tumKatalog}
        </label>
      )}

      <AlanSatiri>
        <Alan etiket={metin.kalkisAraligi} ipucu={metin.kalkisIpucu}>
          <TarihAraligiSecici testId="campaign-departure" giris={kalkis.from} cikis={kalkis.to} setAralik={(from, to) => setKalkis({ from, to })} />
        </Alan>
        <Alan etiket={metin.asgariSepet} ipucu={metin.asgariIpucu}><input value={asgari} inputMode="decimal" onChange={(olay) => setAsgari(olay.target.value)} /></Alan>
      </AlanSatiri>

      <AlanSatiri>
        <Alan etiket={metin.kodBasinaSinir} zorunlu><input value={sinir} inputMode="numeric" onChange={(olay) => setSinir(olay.target.value)} /></Alan>
        <Alan etiket={metin.toplamButce} ipucu={metin.butceIpucu}><input value={butce} inputMode="decimal" onChange={(olay) => setButce(olay.target.value)} /></Alan>
        <Alan etiket={metin.gecerlilik}>
          <TarihAraligiSecici testId="campaign-validity" giris={gecerlilik.from} cikis={gecerlilik.to} setAralik={(from, to) => setGecerlilik({ from, to })} />
        </Alan>
      </AlanSatiri>

      <fieldset>
        <legend>{metin.kod}</legend>
        <AlanSatiri>
          <label><input type="radio" name="kod-kipi" checked={kodKipi === 'BULK'} onChange={() => setKodKipi('BULK')} /> {metin.kodToplu}</label>
          <label><input type="radio" name="kod-kipi" checked={kodKipi === 'SINGLE'} onChange={() => setKodKipi('SINGLE')} /> {metin.kodTek}</label>
          <label><input type="radio" name="kod-kipi" checked={kodKipi === 'NONE'} onChange={() => setKodKipi('NONE')} /> {metin.kodYok}</label>
        </AlanSatiri>
        {kodKipi === 'BULK' ? (
          <AlanSatiri>
            <Alan etiket={metin.onEk}><input data-testid="campaign-prefix" value={onEk} maxLength={12} onChange={(olay) => setOnEk(olay.target.value.toUpperCase())} /></Alan>
            <Alan etiket={metin.adet}><input data-testid="campaign-count" value={adet} inputMode="numeric" onChange={(olay) => setAdet(olay.target.value)} /></Alan>
          </AlanSatiri>
        ) : null}
        {kodKipi === 'SINGLE' ? (
          <AlanSatiri>
            <Alan etiket={metin.kodAdi}><input value={kodAdi} maxLength={64} onChange={(olay) => setKodAdi(olay.target.value.toUpperCase())} /></Alan>
          </AlanSatiri>
        ) : null}
        <p className="muted">{metin.kodOrnek}</p>
      </fieldset>

      <button type="button" className="birincil" disabled={mesgul} data-testid="campaign-submit" onClick={() => void gonder()}>
        {mesgul ? metin.olusturuluyor : metin.olustur}
      </button>
    </section>
  );
}

export function MarketingCampaignPanel({ me }: { me: Me }) {
  const okuyabilir = me.permissions.includes('marketing:campaign:read');
  const yazabilir = me.permissions.includes('marketing:campaign:write');
  const [kaynak, setKaynak] = useState<(typeof KAYNAKLAR)[number]>('ALL');
  const [satirlar, setSatirlar] = useState<CampaignRow[] | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [formAcik, setFormAcik] = useState(false);
  const [seciliId, setSeciliId] = useState<string | null>(null);
  const metin = KAMPANYA_METIN.liste;

  const yukle = useCallback(async () => {
    setHata(null);
    try {
      setSatirlar((await api.campaigns(kaynak)).items);
    } catch (caught) {
      setHata(hataMetni(caught, metin.hata));
    }
  }, [kaynak, metin.hata]);

  useEffect(() => {
    if (okuyabilir) void yukle();
  }, [okuyabilir, yukle]);

  if (!okuyabilir) return null;

  return (
    <>
      <section className="content-section" data-testid="campaigns" aria-labelledby="campaigns-title">
        <div className="section-heading">
          <div>
            <span className="eyebrow">{metin.ustBaslik}</span>
            <h2 id="campaigns-title">{metin.baslik}</h2>
            <p className="muted">{metin.tarif}</p>
          </div>
          <span className="count-badge">{satirlar === null ? TANIMSIZ : `${tamSayi(satirlar.length)} ${metin.sayim}`}</span>
        </div>
        {hata ? <div className="error callout" role="alert">{hata}</div> : null}
        <div className="toolbar">
          <label>
            {metin.kaynak}{' '}
            <select value={kaynak} data-testid="campaign-source" onChange={(olay) => setKaynak(olay.target.value as (typeof KAYNAKLAR)[number])}>
              {KAYNAKLAR.map((deger) => <option key={deger} value={deger}>{KAMPANYA_KAYNAK[deger]}</option>)}
            </select>
          </label>
          <button type="button" onClick={() => void yukle()}>{metin.yenile}</button>
          {yazabilir ? <button type="button" className="birincil" data-testid="campaign-new" onClick={() => setFormAcik(true)}>{metin.yeni}</button> : null}
        </div>
        <div className="table-wrap">
          <table data-testid="campaigns-table">
            <thead>
              <tr>
                <th>{metin.sutunKampanya}</th>
                <th>{metin.sutunKaynak}</th>
                <th className="money">{metin.sutunIndirim}</th>
                <th>{metin.sutunGecerlilik}</th>
                <th className="money">{metin.sutunKod}</th>
                <th className="money">{metin.sutunKullanilan}</th>
                <th>{metin.sutunKalan}</th>
                <th>{metin.sutunDurum}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {satirlar === null ? (
                <tr><td colSpan={9} className="muted" role="status">{metin.yukleniyor}</td></tr>
              ) : satirlar.length === 0 ? (
                <tr><td colSpan={9} className="muted">{metin.bos}</td></tr>
              ) : (
                satirlar.map((satir) => (
                  <tr key={`${satir.kind}-${satir.id}`} data-testid="campaign-row">
                    <td data-label={metin.sutunKampanya}>
                      {satir.name ?? `${metin.influencerAdi} ${satir.id.slice(0, 8)}`}
                      {satir.kind === 'INFLUENCER' ? <><br /><small className="muted">{metin.influencerAlt} {(satir.influencerRef ?? '').slice(0, 8)}</small></> : null}
                      {satir.unpublishedCodes > 0 ? <><br /><small className="muted">{tamSayi(satir.unpublishedCodes)} {metin.yayinlanmayan}</small></> : null}
                    </td>
                    <td data-label={metin.sutunKaynak}>{KAMPANYA_KAYNAK[satir.kind] ?? satir.kind}</td>
                    <td data-label={metin.sutunIndirim} className="money">{indirimYaz(satir.discount)}</td>
                    <td data-label={metin.sutunGecerlilik}>{tarih(satir.validFrom)} - {tarih(satir.validUntil)}</td>
                    <td data-label={metin.sutunKod} className="money">{tamSayi(satir.codeCount)}</td>
                    <td data-label={metin.sutunKullanilan} className="money">{tamSayi(satir.confirmed)}</td>
                    <td data-label={metin.sutunKalan}>{kalanMetni(satir)}</td>
                    <td data-label={metin.sutunDurum}>{KAMPANYA_DURUMU[satir.status] ?? satir.status}</td>
                    <td>{satir.kind === 'INTERNAL' ? <button type="button" onClick={() => setSeciliId(satir.id)}>{metin.detay}</button> : null}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
      {seciliId ? <KampanyaAyrinti id={seciliId} yazabilir={yazabilir} onKapat={() => setSeciliId(null)} onDegisti={() => void yukle()} /> : null}
      {formAcik && yazabilir ? <KampanyaFormu onOlustu={() => void yukle()} onVazgec={() => setFormAcik(false)} /> : null}
    </>
  );
}
